import { useState } from 'react';
import { DateTime } from 'luxon';
import { Pencil, Siren, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { emergencyStatus, endEmergencyNow, removeEmergency, upsertEmergency } from '@/lib/bot-service-hours';
import { EmergencyDialog } from './emergency-dialog';
import { ScopeChip } from './special-days-section';

// Emergency overrides — the one section that does NOT go through the draft/save-bar cycle.
// Every action (Start / save edit / End now / delete) is committed immediately via the
// parent's onCommit: a fresh GET, the emergencies array rebased onto it, a PUT with the
// fresh baseUpdatedAt. Half-finished draft edits elsewhere never block ending an emergency,
// and an emergency PUT never clobbers someone else's concurrent settings save.

function StatusPill({ status }) {
  if (status === 'active') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-semibold text-destructive">
        <span className="size-1.5 animate-pulse rounded-full bg-destructive" aria-hidden />
        Active
      </span>
    );
  }
  if (status === 'upcoming') {
    return (
      <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-300">
        Upcoming
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10.5px] text-muted-foreground">
      Ended
    </span>
  );
}

export function EmergenciesSection({
  emergencies = [],
  gateways = [],
  placeholders = [],
  timezone,
  canEdit = false,
  busy = false,
  nowISO,
  onCommit,
}) {
  const [dialog, setDialog] = useState(null); // { emergency: object|null } while open
  const [showPast, setShowPast] = useState(false);

  const zone = timezone || 'UTC';
  const format = (iso) => {
    const dt = DateTime.fromISO(iso).setZone(zone);
    return dt.isValid ? dt.toFormat('ccc d LLL yyyy HH:mm') : iso || '—';
  };

  const sorted = [...emergencies].sort((a, b) => (a.from === b.from ? (a.id < b.id ? -1 : 1) : a.from < b.from ? -1 : 1));
  const withStatus = sorted.map((emergency) => ({
    emergency,
    status: emergencyStatus(emergency, nowISO),
  }));
  const past = withStatus.filter((entry) => entry.status === 'past');
  const current = withStatus.filter((entry) => entry.status !== 'past');

  const handleDialogSave = async (emergency) => {
    const ok = await onCommit(
      (settings) => upsertEmergency(settings, emergency),
      emergency.id ? 'Emergency override updated.' : 'Emergency override started.',
    );
    if (ok) setDialog(null);
  };

  const renderRow = ({ emergency, status }) => (
    <div
      key={emergency.id}
      className={cn(
        'flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5',
        status === 'active' ? 'border-destructive/50' : 'border-border',
        status === 'past' && 'opacity-60',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={status} />
          <span className="truncate text-[13px] font-semibold text-foreground">{emergency.label}</span>
          <ScopeChip gatewayBotIds={emergency.gatewayBotIds} gateways={gateways} />
          <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10.5px] text-muted-foreground">
            {emergency.mode === 'silent' ? 'Silent' : 'Reply'}
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground">
          {format(emergency.from)} → {emergency.until ? format(emergency.until) : 'until further notice'}
        </p>
      </div>
      {canEdit && status === 'active' ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 shrink-0 border-destructive/40 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
          disabled={busy}
          onClick={() =>
            onCommit(
              (settings) => endEmergencyNow(settings, emergency.id, new Date().toISOString()),
              'Emergency override ended.',
            )
          }
        >
          End now
        </Button>
      ) : null}
      {canEdit && status === 'upcoming' ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={`Edit ${emergency.label}`}
          disabled={busy}
          onClick={() => setDialog({ emergency })}
        >
          <Pencil className="size-3.5 text-muted-foreground" aria-hidden />
        </Button>
      ) : null}
      {canEdit ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={`Delete ${emergency.label}`}
          disabled={busy}
          onClick={() =>
            onCommit((settings) => removeEmergency(settings, emergency.id), 'Emergency override deleted.')
          }
        >
          <Trash2 className="size-3.5 text-muted-foreground" aria-hidden />
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className="flex flex-col gap-1.5">
      {current.length === 0 && past.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-5 text-center text-xs text-muted-foreground">
          No emergency overrides.
        </p>
      ) : null}
      {current.map(renderRow)}
      {past.length > 0 ? (
        <button
          type="button"
          className="self-start px-1 py-0.5 text-[11px] text-muted-foreground transition hover:text-foreground"
          onClick={() => setShowPast((value) => !value)}
        >
          {showPast
            ? 'Hide ended overrides'
            : `Show ${past.length} ended override${past.length === 1 ? '' : 's'}`}
        </button>
      ) : null}
      {showPast ? past.map(renderRow) : null}
      {current.length > 0 || past.length > 0 ? (
        <p className="text-[11px] text-muted-foreground">Times in {String(zone).replace(/_/g, ' ')}.</p>
      ) : null}
      {canEdit ? (
        <div>
          <Button type="button" size="sm" disabled={busy} onClick={() => setDialog({ emergency: null })}>
            <Siren className="mr-1.5 size-4" aria-hidden />
            Start emergency
          </Button>
        </div>
      ) : null}
      <EmergencyDialog
        open={Boolean(dialog)}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
        emergency={dialog?.emergency || null}
        gateways={gateways}
        placeholders={placeholders}
        timezone={zone}
        busy={busy}
        onSave={handleDialogSave}
      />
    </div>
  );
}
